// <dispatch-countdown> shows today's dispatch status against a cut-off time in Europe/Istanbul.
// Weekends and Turkish public holidays are skipped. Copy comes from data-* attributes ({{ time }} / {{ date }} placeholders).
const ANNUAL_HOLIDAYS = new Set(['01-01', '04-23', '05-01', '05-19', '07-15', '08-30', '10-29']);
const SPECIAL_HOLIDAYS = new Set([
  '2026-03-19', '2026-03-20', '2026-03-21', '2026-03-22',
  '2026-05-26', '2026-05-27', '2026-05-28', '2026-05-29', '2026-05-30',
  '2026-10-28',
]);

const two = (value) => String(value).padStart(2, '0');
const dateKey = (date) => `${date.getUTCFullYear()}-${two(date.getUTCMonth() + 1)}-${two(date.getUTCDate())}`;
const workingDay = (date) =>
  ![0, 6].includes(date.getUTCDay()) && !ANNUAL_HOLIDAYS.has(dateKey(date).slice(5)) && !SPECIAL_HOLIDAYS.has(dateKey(date));
const fill = (template, key, value) => (template || '').replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), value);

class DispatchCountdown extends HTMLElement {
  #timer = 0;
  #onVisible = () => { if (!document.hidden) this.#update(); };

  connectedCallback() {
    this.#update();
    this.#timer = setInterval(() => this.#update(), 30000);
    document.addEventListener('visibilitychange', this.#onVisible);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
    document.removeEventListener('visibilitychange', this.#onVisible);
  }

  #update() {
    const message = this.querySelector('[data-dispatch-message]');
    const countdown = this.querySelector('[data-dispatch-countdown]');
    if (!message || !countdown) return;
    const d = this.dataset;

    const clock = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
    });
    const v = Object.fromEntries(
      clock.formatToParts(new Date()).filter((p) => p.type !== 'literal').map((p) => [p.type, Number(p.value)]),
    );
    const today = new Date(Date.UTC(v.year, v.month - 1, v.day));
    const now = (v.hour % 24) * 3600 + v.minute * 60 + v.second;
    const cutoff = (Number(d.hour) || 0) * 3600 + (Number(d.minute) || 0) * 60;

    if (workingDay(today) && now < cutoff) {
      const minutes = Math.ceil((cutoff - now) / 60);
      const hours = Math.floor(minutes / 60);
      const rest = minutes % 60;
      const time = [hours ? `${hours} ${d.hours}` : '', rest || !hours ? `${rest} ${d.minutes}` : ''].filter(Boolean).join(' ');
      // One sentence like the other states: "2 saat 15 dk içinde sipariş verin, bugün kargoya verelim".
      message.textContent = fill(d.today, 'time', time);
      countdown.hidden = true;
      return;
    }

    countdown.hidden = true;
    const next = new Date(today);
    if (workingDay(today)) next.setUTCDate(next.getUTCDate() + 1);
    while (!workingDay(next)) next.setUTCDate(next.getUTCDate() + 1);
    const tomorrow = new Date(today);
    tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
    const label = new Intl.DateTimeFormat(document.documentElement.lang || 'tr-TR', { timeZone: 'UTC', day: 'numeric', month: 'long' });
    message.textContent = dateKey(next) === dateKey(tomorrow) ? d.tomorrow : fill(d.date, 'date', label.format(next));
  }
}

if (!customElements.get('dispatch-countdown')) customElements.define('dispatch-countdown', DispatchCountdown);
